import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Loader,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconInfoCircle, IconMinus, IconPlus } from '@tabler/icons-react';
import { ThemeToggle } from '../components/ThemeToggle';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../lib/env';
import {
  isFormOpenNow,
  findProfileSuggestions,
  isMultiValueField,
  isPersonListField,
  isSelectionField,
  isStaticField,
  normalizeFormPersonListEntries,
  normalizePersonListEntries,
  personListEntryFromProfile,
  personListEntryFromText,
  personListUsesProfileSuggestions,
} from '../lib/forms';
import type { FormFieldRow, FormPersonListEntry, FormProfileSuggestion, FormRow } from '../lib/forms';
import { supabase } from '../lib/supabaseClient';

type PublicFormPayload = {
  form: FormRow;
  fields: FormFieldRow[];
};

function mapFormRow(row: Record<string, unknown>): FormRow {
  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? ''),
    slug: String(row.slug ?? ''),
    short_description: String(row.short_description ?? ''),
    intro_text: String(row.intro_text ?? ''),
    submit_button_label: String(row.submit_button_label ?? 'Absenden'),
    confirmation_title: String(row.confirmation_title ?? 'Vielen Dank'),
    confirmation_message: String(row.confirmation_message ?? 'Deine Anmeldung wurde gespeichert.'),
    allow_submission_edit: row.allow_submission_edit === true,
    status: (String(row.status ?? 'draft') as FormRow['status']),
    starts_at: row.starts_at ? String(row.starts_at) : null,
    ends_at: row.ends_at ? String(row.ends_at) : null,
    show_on_homepage: row.show_on_homepage === true,
    homepage_title: row.homepage_title ? String(row.homepage_title) : null,
    homepage_text: row.homepage_text ? String(row.homepage_text) : null,
    homepage_cta_label: row.homepage_cta_label ? String(row.homepage_cta_label) : null,
    push_body: row.push_body ? String(row.push_body) : null,
    published_at: row.published_at ? String(row.published_at) : null,
    notification_sent_at: row.notification_sent_at ? String(row.notification_sent_at) : null,
    notification_sent_by: row.notification_sent_by ? String(row.notification_sent_by) : null,
    last_status: row.last_status ? String(row.last_status) as FormRow['status'] : null,
    public_url: String(row.public_url ?? ''),
    created_by: String(row.created_by ?? ''),
    updated_by: row.updated_by ? String(row.updated_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function mapFieldRow(row: Record<string, unknown>): FormFieldRow {
  return {
    id: String(row.id ?? ''),
    form_id: String(row.form_id ?? ''),
    field_key: String(row.field_key ?? ''),
    field_type: String(row.field_type ?? 'text_input') as FormFieldRow['field_type'],
    label: String(row.label ?? ''),
    help_text: String(row.help_text ?? ''),
    placeholder: String(row.placeholder ?? ''),
    static_text: String(row.static_text ?? ''),
    options: Array.isArray(row.options) ? row.options.map((entry) => String(entry)) : [],
    field_settings: row.field_settings && typeof row.field_settings === 'object' && !Array.isArray(row.field_settings)
      ? row.field_settings as Record<string, unknown>
      : {},
    is_required: row.is_required === true,
    sort_order: Number(row.sort_order ?? 0),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function valueAsString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function formatWindow(form: FormRow): string {
  const parts: string[] = [];
  if (form.starts_at) {
    parts.push(`Start ${new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(form.starts_at))}`);
  }
  if (form.ends_at) {
    parts.push(`Ende ${new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(form.ends_at))}`);
  }
  return parts.join(' · ');
}

export function PublicFormPage() {
  const { slug = '' } = useParams();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    document.title = slug ? `Anmeldung – ${slug}` : 'Anmeldung';
  }, [slug]);

  useEffect(() => {
    setValues({});
    setErrors({});
    setHoneypot('');
    setSubmitted(null);
  }, [slug]);

  const formQ = useQuery({
    queryKey: ['public-form', slug],
    enabled: slug.trim().length > 0,
    queryFn: async (): Promise<PublicFormPayload> => {
      const { data: formData, error: formError } = await supabase
        .from('forms')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (formError) throw formError;
      if (!formData) throw new Error('Formular nicht gefunden.');

      const form = mapFormRow(formData as Record<string, unknown>);

      const { data: fieldData, error: fieldError } = await supabase
        .from('form_fields')
        .select('*')
        .eq('form_id', form.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (fieldError) throw fieldError;

      return {
        form,
        fields: ((fieldData ?? []) as Array<Record<string, unknown>>).map(mapFieldRow),
      };
    },
  });

  const publicForm = formQ.data?.form ?? null;
  const fields = formQ.data?.fields ?? [];
  const canSubmit = publicForm ? isFormOpenNow(publicForm) : false;
  const hasProfilePersonList = fields.some((field) => personListUsesProfileSuggestions(field));
  const adminPreviewQ = useQuery({
    queryKey: ['public-form-admin-preview', publicForm?.id, canSubmit],
    enabled: Boolean(publicForm) && !canSubmit,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('can_manage_forms', {});
      if (error) return false;
      return data === true;
    },
  });
  const canSubmitAsPreview = !canSubmit && adminPreviewQ.data === true;
  const canSubmitEffective = canSubmit || canSubmitAsPreview;

  const profileSuggestionsQ = useQuery({
    queryKey: ['public-form-profile-suggestions', publicForm?.id],
    enabled: hasProfilePersonList,
    queryFn: async (): Promise<Array<Record<string, unknown>>> => {
      const { data, error } = await supabase
        .from('public_profiles')
        .select('id, first_name, last_name, nickname')
        .order('first_name', { ascending: true })
        .order('last_name', { ascending: true });
      if (error) return [];
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  // Diagnostic: detect circular structures early to avoid "Maximum call stack size exceeded" errors
  function hasCycle(obj: unknown): boolean {
    try {
      const seen = new WeakSet();
      function visit(value: unknown): boolean {
        if (value && typeof value === 'object') {
          // Objects and arrays
          if (seen.has(value as object)) return true;
          seen.add(value as object);
          if (Array.isArray(value)) {
            for (const v of value) if (visit(v)) return true;
            return false;
          }
          for (const key of Object.keys(value as Record<string, unknown>)) {
            // @ts-ignore
            if (visit((value as Record<string, unknown>)[key])) return true;
          }
        }
        return false;
      }
      return visit(obj);
    } catch (e) {
      // If traversal fails, be conservative and report no-cycle
      return false;
    }
  }

  useEffect(() => {
    try {
      const cycleInFields = hasCycle(fields);
      const cycleInValues = hasCycle(values);
      if (cycleInFields || cycleInValues) {
        const info = {
          cycleInFields,
          cycleInValues,
          fieldsLen: fields.length,
          valuesKeys: Object.keys(values).length,
        };
        // Log to console for local debugging
        // eslint-disable-next-line no-console
        console.error('Detected potential circular structure in public form payload', info);
        // Also send to Sentry for production visibility
        // Use simple message instead of JSON.stringify to avoid stack overflow if data is circular
        Sentry.captureMessage(
          `Detected potential circular structure: cycleInFields=${cycleInFields} cycleInValues=${cycleInValues} fieldsLen=${info.fieldsLen} valuesKeys=${info.valuesKeys}`,
          'error'
        );
      }
    } catch (err) {
      // ignore diagnostics errors
    }
  // Intentionally minimal dependencies: run when fields or values change
  }, [fields, values]);

  const validationErrors = useMemo(() => {
    const next: Record<string, string> = {};

    for (const field of fields) {
      if (isStaticField(field.field_type)) continue;

      const rawValue = values[field.field_key];

      if (field.field_type === 'checkbox') {
        if (field.is_required && rawValue !== true) {
          next[field.field_key] = 'Dieses Feld ist erforderlich.';
        }
        continue;
      }

      if (isPersonListField(field.field_type)) {
        const entries = normalizePersonListEntries(rawValue);
        if (field.is_required && entries.length === 0) {
          next[field.field_key] = 'Bitte mindestens einen Namen eintragen.';
        }
        continue;
      }

      if (isMultiValueField(field.field_type)) {
        const list = Array.isArray(rawValue) ? rawValue.map((entry) => String(entry).trim()).filter(Boolean) : [];
        if (field.is_required && list.length === 0) {
          next[field.field_key] = 'Bitte mindestens eine Option wählen.';
          continue;
        }
        if (list.some((entry) => !field.options.includes(entry))) {
          next[field.field_key] = 'Bitte nur gültige Optionen wählen.';
        }
        continue;
      }

      const value = valueAsString(rawValue).trim();
      if (field.is_required && value.length === 0) {
        next[field.field_key] = 'Dieses Feld ist erforderlich.';
        continue;
      }
      if (!value) continue;

      if (isSelectionField(field.field_type) && !field.options.includes(value)) {
        next[field.field_key] = 'Bitte eine gültige Option wählen.';
        continue;
      }

      if (field.field_type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(value)) {
        next[field.field_key] = 'Bitte eine gültige E-Mail-Adresse eingeben.';
        continue;
      }

      if (field.field_type === 'phone' && !/^[0-9+()\/\-\s]{6,30}$/.test(value)) {
        next[field.field_key] = 'Bitte eine gültige Telefonnummer eingeben.';
        continue;
      }

      if (field.field_type === 'number' && Number.isNaN(Number(value))) {
        next[field.field_key] = 'Bitte eine gültige Zahl eingeben.';
        continue;
      }

      if (field.field_type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        next[field.field_key] = 'Bitte ein gültiges Datum eingeben.';
      }
    }

    return next;
  }, [fields, values]);

  const submitM = useMutation({
    mutationFn: async () => {
      if (!publicForm) throw new Error('Formular ist nicht geladen.');
      if (!canSubmitEffective) throw new Error('Formular ist aktuell nicht aktiv.');

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        throw new Error('Bitte die markierten Felder prüfen.');
      }

      const payload = {
        slug: publicForm.slug,
        answers: values,
        company: honeypot,
        source: 'web',
        preview: canSubmitAsPreview,
      };
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token || SUPABASE_ANON_KEY;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/public-forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const body = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        confirmationTitle?: string;
        confirmationMessage?: string;
      };

      if (!response.ok || body.ok !== true) {
        throw new Error(body.error || 'Formular konnte nicht gespeichert werden.');
      }

      return {
        title: body.confirmationTitle || publicForm.confirmation_title,
        message: body.confirmationMessage || publicForm.confirmation_message,
      };
    },
    onSuccess: (result) => {
      setSubmitted(result);
    },
    onError: (error) => {
      setErrors(validationErrors);
      const message = error instanceof Error ? error.message : 'Formular konnte nicht gespeichert werden.';
      setErrors((prev) => ({
        ...prev,
        __form: message,
      }));
    },
  });

  if (formQ.isLoading) {
    return (
      <Box px="md" py="xl">
        <Group justify="center">
          <Loader color="brand" />
        </Group>
      </Box>
    );
  }

  if (formQ.error || !publicForm) {
    return (
      <Box px="md" py="xl">
        <Card radius="lg" p="lg" className="contrast-page contrast-surface">
          <Stack gap="sm">
            <Group gap="sm">
              <IconAlertCircle size={18} />
              <Title order={3}>Formular nicht verfügbar</Title>
            </Group>
            <Text c="dimmed">
              Dieses Formular ist entweder nicht veröffentlicht, bereits abgelaufen oder die URL ist ungültig.
            </Text>
          </Stack>
        </Card>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box px="md" py="xl">
        <Card radius="lg" p="lg" className="contrast-page contrast-surface">
          <Stack gap="md">
            <Group gap="sm">
              <IconCheck size={18} />
              <Title order={2}>{submitted.title}</Title>
            </Group>
            <Text>{submitted.message}</Text>
          </Stack>
        </Card>
      </Box>
    );
  }

  const listValueFor = (key: string): string[] => {
    const raw = values[key];
    if (!Array.isArray(raw)) return [];
    return raw.map((entry: unknown) => String(entry));
  };

  const personListValueFor = (key: string): FormPersonListEntry[] => {
    const raw = values[key];
    const entries = normalizeFormPersonListEntries(raw);
    return entries.length > 0 ? entries : [{ displayName: '', userId: null, source: 'manual' }];
  };

  const profileSuggestionsFor = (query: string): FormProfileSuggestion[] => (
    findProfileSuggestions(query, profileSuggestionsQ.data ?? [])
  );

  const updatePersonListEntry = (fieldKey: string, entries: FormPersonListEntry[], index: number, entry: FormPersonListEntry) => {
    const nextEntries = entries.slice();
    nextEntries[index] = entry;
    setErrors((prev) => ({ ...prev, [fieldKey]: '' }));
    setValues((prev) => ({ ...prev, [fieldKey]: nextEntries.filter((item) => item.displayName.trim()) }));
  };

  return (
    <Box px="md" py="xl" maw={880} mx="auto" className="contrast-page app-fade-in">
      <Stack gap="lg">
        <Card radius="lg" p="lg" className="contrast-surface">
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Box>
                <Title order={1}>{publicForm.title}</Title>
                {publicForm.short_description.trim().length > 0 && (
                  <Text c="dimmed" mt={4}>
                    {publicForm.short_description}
                  </Text>
                )}
              </Box>
              <Group gap="xs" align="center" wrap="nowrap">
                <ThemeToggle />
                <Badge color={canSubmit ? 'teal' : 'yellow'} variant="light">
                  {canSubmit ? 'Aktiv' : canSubmitAsPreview ? 'Admin-Vorschau' : 'Vorschau'}
                </Badge>
              </Group>
            </Group>

            {publicForm.intro_text.trim().length > 0 && (
              <Text>{publicForm.intro_text}</Text>
            )}

            {formatWindow(publicForm) && (
              <Group gap="xs">
                <IconInfoCircle size={16} />
                <Text size="sm" c="dimmed">
                  {formatWindow(publicForm)}
                </Text>
              </Group>
            )}
          </Stack>
        </Card>

        {!canSubmit && (
          <Alert color="yellow" icon={<IconInfoCircle size={16} />} radius="lg">
            {canSubmitAsPreview
              ? 'Dieses Formular ist nur als Admin-Vorschau geöffnet. Absenden speichert eine Testeinreichung.'
              : 'Dieses Formular ist aktuell nicht aktiv. Admins können es hier als Vorschau sehen, öffentlich absendbar ist es erst im Status „veröffentlicht“ innerhalb des Aktivitätsfensters.'}
          </Alert>
        )}

        {errors.__form && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} radius="lg">
            {errors.__form}
          </Alert>
        )}

        <Card radius="lg" p="lg" className="contrast-surface">
          <Stack gap="md">
            {fields.map((field) => {
              if (field.field_type === 'section') {
                return (
                  <Stack key={field.id} gap={6}>
                    <Divider />
                    <Title order={3}>{field.label}</Title>
                    {field.help_text.trim().length > 0 && (
                      <Text c="dimmed" size="sm">
                        {field.help_text}
                      </Text>
                    )}
                  </Stack>
                );
              }

              if (field.field_type === 'text') {
                return (
                  <Card key={field.id} p="md" radius="md" className="contrast-surface-soft">
                    <Stack gap={6}>
                      {field.label.trim().length > 0 && (
                        <Text fw={600}>{field.label}</Text>
                      )}
                      <Text>{field.static_text}</Text>
                    </Stack>
                  </Card>
                );
              }

              const commonProps = {
                key: field.id,
                label: field.label,
                description: field.help_text || undefined,
                error: errors[field.field_key] || undefined,
              };

              if (field.field_type === 'textarea') {
                return (
                  <Textarea
                    {...commonProps}
                    minRows={3}
                    placeholder={field.placeholder || undefined}
                    value={valueAsString(values[field.field_key])}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                      setValues((prev) => ({ ...prev, [field.field_key]: nextValue }));
                    }}
                  />
                );
              }

              if (field.field_type === 'select') {
                return (
                  <Select
                    {...commonProps}
                    searchable
                    clearable={!field.is_required}
                    data={field.options.map((entry) => ({ value: entry, label: entry }))}
                    value={valueAsString(values[field.field_key]) || null}
                    placeholder={field.placeholder || undefined}
                    onChange={(value) => {
                      setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                      setValues((prev) => ({ ...prev, [field.field_key]: value ?? '' }));
                    }}
                  />
                );
              }

              if (field.field_type === 'multiselect') {
                return (
                  <MultiSelect
                    {...commonProps}
                    searchable
                    clearable={!field.is_required}
                    data={field.options.map((entry) => ({ value: entry, label: entry }))}
                    value={listValueFor(field.field_key)}
                    placeholder={field.placeholder || undefined}
                    onChange={(value) => {
                      setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                      setValues((prev) => ({ ...prev, [field.field_key]: value }));
                    }}
                  />
                );
              }

              if (field.field_type === 'radio') {
                return (
                  <Stack key={field.id} gap={4}>
                    <Text fw={500}>
                      {field.label}
                      {field.is_required ? ' *' : ''}
                    </Text>
                    {field.help_text && (
                      <Text size="sm" c="dimmed">
                        {field.help_text}
                      </Text>
                    )}
                    <Radio.Group
                      value={valueAsString(values[field.field_key])}
                      onChange={(value) => {
                        setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                        setValues((prev) => ({ ...prev, [field.field_key]: value }));
                      }}
                    >
                      <Stack gap="xs" mt={6}>
                        {field.options.map((option) => (
                          <Radio key={`${field.id}-${option}`} value={option} label={option} />
                        ))}
                      </Stack>
                    </Radio.Group>
                    {errors[field.field_key] && (
                      <Text size="sm" c="red">
                        {errors[field.field_key]}
                      </Text>
                    )}
                  </Stack>
                );
              }

              if (field.field_type === 'checkbox_group') {
                return (
                  <Stack key={field.id} gap={4}>
                    <Text fw={500}>
                      {field.label}
                      {field.is_required ? ' *' : ''}
                    </Text>
                    {field.help_text && (
                      <Text size="sm" c="dimmed">
                        {field.help_text}
                      </Text>
                    )}
                    <Checkbox.Group
                      value={listValueFor(field.field_key)}
                      onChange={(value) => {
                        setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                        setValues((prev) => ({ ...prev, [field.field_key]: value }));
                      }}
                    >
                      <Stack gap="xs" mt={6}>
                        {field.options.map((option) => (
                          <Checkbox key={`${field.id}-${option}`} value={option} label={option} />
                        ))}
                      </Stack>
                    </Checkbox.Group>
                    {errors[field.field_key] && (
                      <Text size="sm" c="red">
                        {errors[field.field_key]}
                      </Text>
                    )}
                  </Stack>
                );
              }

              if (field.field_type === 'person_list') {
                const entries = personListValueFor(field.field_key);
                const useProfileSuggestions = personListUsesProfileSuggestions(field);

                return (
                  <Stack key={field.id} gap={4}>
                    <Text fw={500}>
                      {field.label}
                      {field.is_required ? ' *' : ''}
                    </Text>
                    {field.help_text && (
                      <Text size="sm" c="dimmed">
                        {field.help_text}
                      </Text>
                    )}
                    <Stack gap="xs">
                      {entries.map((entry, index) => (
                        <Group key={`${field.id}-${index}`} align="end" wrap="nowrap">
                          <Box style={{ flex: 1 }}>
                            {useProfileSuggestions ? (
                              <Autocomplete
                                label={index === 0 ? 'Name' : undefined}
                                placeholder={field.placeholder || `Person ${index + 1}`}
                                value={entry.displayName}
                                data={profileSuggestionsFor(entry.displayName).map((profile) => profile.displayName)}
                                onChange={(value) => updatePersonListEntry(
                                  field.field_key,
                                  entries,
                                  index,
                                  personListEntryFromText(value)
                                )}
                                onOptionSubmit={(value) => {
                                  const profile = profileSuggestionsFor(value)
                                    .find((candidate) => candidate.displayName === value);
                                  updatePersonListEntry(
                                    field.field_key,
                                    entries,
                                    index,
                                    profile ? personListEntryFromProfile(profile) : personListEntryFromText(value)
                                  );
                                }}
                              />
                            ) : (
                              <TextInput
                                label={index === 0 ? 'Name' : undefined}
                                placeholder={field.placeholder || `Person ${index + 1}`}
                                value={entry.displayName}
                                onChange={(event) => updatePersonListEntry(
                                  field.field_key,
                                  entries,
                                  index,
                                  personListEntryFromText(event.currentTarget.value)
                                )}
                              />
                            )}
                            {entry.userId && (
                              <Text size="xs" c="dimmed" mt={4}>
                                Aus App-Profil ausgewählt
                              </Text>
                            )}
                          </Box>
                          <ActionIcon
                            variant="light"
                            color="red"
                            aria-label="Name entfernen"
                            mb={index === 0 ? 2 : 0}
                            onClick={() => {
                              const nextEntries = entries.filter((_, entryIndex) => entryIndex !== index);
                              setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                              setValues((prev) => ({
                                ...prev,
                                [field.field_key]: nextEntries.filter((item) => item.displayName.trim()),
                              }));
                            }}
                          >
                            <IconMinus size={16} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                    {errors[field.field_key] && (
                      <Text size="sm" c="red">
                        {errors[field.field_key]}
                      </Text>
                    )}
                    <Group justify="flex-start">
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => {
                          setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                          setValues((prev) => ({
                            ...prev,
                            [field.field_key]: [...entries, { displayName: '', userId: null, source: 'manual' }],
                          }));
                        }}
                      >
                        Person hinzufügen
                      </Button>
                    </Group>
                  </Stack>
                );
              }

              if (field.field_type === 'checkbox') {
                return (
                  <Checkbox
                    {...commonProps}
                    checked={values[field.field_key] === true}
                    label={`${field.label}${field.is_required ? ' *' : ''}`}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.checked;
                      setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                      setValues((prev) => ({ ...prev, [field.field_key]: nextValue }));
                    }}
                  />
                );
              }

              const inputType =
                field.field_type === 'email'
                  ? 'email'
                  : field.field_type === 'phone'
                    ? 'tel'
                    : field.field_type === 'number'
                      ? 'number'
                      : field.field_type === 'date'
                        ? 'date'
                        : 'text';

              return (
                <TextInput
                  {...commonProps}
                  type={inputType}
                  placeholder={field.placeholder || undefined}
                  value={valueAsString(values[field.field_key])}
                  onChange={(event) => {
                    const nextValue = event.currentTarget.value;
                    setErrors((prev) => ({ ...prev, [field.field_key]: '' }));
                    setValues((prev) => ({ ...prev, [field.field_key]: nextValue }));
                  }}
                />
              );
            })}

            <TextInput
              label="Nicht ausfüllen"
              value={honeypot}
              onChange={(event) => setHoneypot(event.currentTarget.value)}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            <Group justify="flex-end">
              <Button
                size="md"
                leftSection={<IconCheck size={16} />}
                loading={submitM.isPending}
                disabled={!canSubmitEffective}
                onClick={() => {
                  setErrors(validationErrors);
                  void submitM.mutateAsync();
                }}
              >
                {publicForm.submit_button_label || 'Absenden'}
              </Button>
            </Group>
          </Stack>
        </Card>

        {fields.filter((field) => !isStaticField(field.field_type)).length > 0 && (
          <Card radius="lg" p="lg" className="contrast-surface-soft">
            <Stack gap="sm">
              <Title order={4}>Hinweis zu Anmeldung und Datenschutz</Title>
              <Text size="sm" c="dimmed">
                Pflichtfelder sind mit * markiert. Deine Angaben werden ausschließlich für die Auswertung deiner Anmeldung sowie für die
                Planung und Organisation des Aufenthalts gespeichert und verarbeitet. Sobald sie dafür nicht mehr erforderlich sind, werden
                sie im Rahmen der geltenden Datenschutzvorgaben wieder entfernt.
              </Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
